/**
 * PreventivoAutoPage.jsx — TASK 5: Wizard RC Auto pubblico completo a 8 step.
 *
 * Step:
 *   1) Dati Personali (con auto-fill da CF)
 *   2) Residenza
 *   3) Patente
 *   4) Veicolo
 *   4.5) Documenti opzionali (skip ammesso)
 *   5) Storia Assicurativa
 *   6) Coperture
 *   7) Contatti + OTP telefono
 *   8) Riepilogo + Consensi
 *
 * Salvataggio bozza: localStorage + backend `/draft/save` ogni step.
 * Riprende da `?draft=<token>`.
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import StepNav from "@/components/preventivo-auto/StepNav";
import PreventivassMeter from "@/components/preventivo-auto/PreventivassMeter";
import LegalLinkBubble from "@/components/LegalLink";
import PrefillWelcomeBanner from "@/components/auth/PrefillWelcomeBanner";
import useClientProfilePrefill from "@/hooks/useClientProfilePrefill";
import AiAutofillBadge from "@/components/AiAutofillBadge";
import AiOcrOptOutToggle, { isAiOcrOptedOut } from "@/components/AiOcrOptOutToggle";
import MupConsegnaModal from "@/components/MupConsegnaModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Lock, ShieldCheck, Upload, FileText, Phone,
  Loader2, Sparkles, X, Check, AlertTriangle, Send, FileCheck2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api/public/preventivo-auto";
const DRAFT_KEY = "colucci_preventivo_auto_draft_v2";

const STEPS = [
  { id: "personali", label: "Personali" },
  { id: "residenza", label: "Residenza" },
  { id: "patente", label: "Patente" },
  { id: "veicolo", label: "Veicolo" },
  { id: "documenti", label: "Documenti" },
  { id: "storia", label: "Storia" },
  { id: "coperture", label: "Coperture" },
  { id: "contatti", label: "Contatti" },
  { id: "riepilogo", label: "Riepilogo" },
];

const SESSI = [{ v: "M", l: "Maschio" }, { v: "F", l: "Femmina" }];
const STATI_CIVILI = [
  ["celibe", "Celibe/Nubile"], ["coniugato", "Coniugato/a"],
  ["convivente", "Convivente"], ["divorziato", "Divorziato/a"],
  ["vedovo", "Vedovo/a"], ["altro", "Altro"],
];
const TITOLI = [
  ["licenza_media", "Licenza media"], ["diploma", "Diploma"],
  ["laurea_triennale", "Laurea triennale"],
  ["laurea_magistrale", "Laurea magistrale"], ["dottorato", "Dottorato"],
  ["altro", "Altro"],
];
const CATEGORIE_PATENTE = ["A", "A1", "A2", "B", "BE", "C", "CE", "D", "DE"];
const ALIMENTAZIONI = [
  ["benzina", "Benzina"], ["diesel", "Diesel"],
  ["ibrida_benzina", "Ibrida benzina"], ["ibrida_diesel", "Ibrida diesel"],
  ["elettrica", "Elettrica"], ["gpl", "GPL"], ["metano", "Metano"],
  ["altro", "Altro"],
];
const USI = [["privato", "Privato"], ["promiscuo", "Promiscuo"],
              ["professionale", "Professionale"]];
const GUIDA = [["esclusiva", "Esclusiva"], ["libera", "Libera"],
                ["esperta", "Esperta (>25 anni)"]];
const TIPI_RICHIESTA = [
  ["nuova_polizza", "Prima polizza"],
  ["rinnovo", "Rinnovo stessa compagnia"],
  ["cambio_compagnia", "Cambio compagnia"],
];

const DOC_TYPES = [
  { v: "carta_identita", l: "Carta d'identità" },
  { v: "patente", l: "Patente" },
  { v: "libretto", l: "Libretto di circolazione" },
  { v: "polizza_corrente", l: "Polizza in corso" },
];

// Default state
const EMPTY = {
  anagrafica: {
    nome: "", cognome: "", codice_fiscale: "", data_nascita: "",
    luogo_nascita: "", sesso: "", stato_civile: "", titolo_studio: "",
    professione: "",
  },
  residenza: { via: "", civico: "", cap: "", citta: "", provincia: "" },
  patente: { numero: "", categoria: "B", data_rilascio: "", data_scadenza: "" },
  veicolo: {
    targa: "", tipologia: "auto", marca: "", modello: "", allestimento: "",
    anno_immatricolazione: "", mese_immatricolazione: 1,
    alimentazione: "", kw_potenza: "", cilindrata: "",
    uso: "privato", percorrenza_annua_km: 15000, antifurto: false,
    antifurto_tipo: "", tipologia_guida: "esclusiva",
  },
  storia: {
    tipo_richiesta: "", compagnia_attuale: "",
    data_scadenza_polizza: "", data_decorrenza_nuova: "",
    classe_merito_cu: "", sinistri_5anni: 0, anni_continuativi: 0,
  },
  coperture: {
    rca: true, massimale_rca_eur: 6000000,
    furto_incendio: false, kasko: false, mini_kasko: false,
    cristalli: false, assistenza_stradale: false, tutela_legale: false,
    infortuni_conducente: false, atti_vandalici: false, eventi_naturali: false,
  },
  contatti: { email: "", telefono: "" },
  consensi: { privacy: false, mup: false, marketing: false, profilazione: false },
};

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------
// FASE 4.2.11.11 fix bug P0 — TUTTE le rotte del wizard sono montate sotto
// /api/public/preventivo-auto (vedi backend/routes/public_preventivo_auto.py).
// L'agente precedente aveva omesso il prefix /api causando 404 silenziosi
// (l'SPA serviva l'HTML come fallback su qualsiasi path non-/api).
const PUBLIC_PREFIX = "/api/public/preventivo-auto";

async function api(path, opts = {}) {
  const r = await fetch(API + PUBLIC_PREFIX + path, {
    method: opts.method || "GET",
    credentials: "include",
    headers: opts.body && !(opts.body instanceof FormData)
      ? { "Content-Type": "application/json" } : {},
    body: opts.body
      ? (opts.body instanceof FormData ? opts.body : JSON.stringify(opts.body))
      : undefined,
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw { status: r.status, body: j };
  return j;
}

// ---------------------------------------------------------------------------
// Validation per step
// ---------------------------------------------------------------------------
function validateStep(idx, s) {
  const errs = {};
  if (idx === 0) {
    const a = s.anagrafica;
    if (!a.nome || a.nome.length < 2) errs.nome = "Nome obbligatorio";
    if (!a.cognome || a.cognome.length < 2) errs.cognome = "Cognome obbligatorio";
    if (!a.codice_fiscale || a.codice_fiscale.length !== 16)
      errs.codice_fiscale = "Codice fiscale di 16 caratteri";
    if (!a.data_nascita) errs.data_nascita = "Data nascita obbligatoria";
    if (!a.luogo_nascita) errs.luogo_nascita = "Luogo nascita obbligatorio";
    if (!a.sesso) errs.sesso = "Sesso obbligatorio";
    if (!a.stato_civile) errs.stato_civile = "Stato civile obbligatorio";
    if (!a.titolo_studio) errs.titolo_studio = "Titolo studio obbligatorio";
    if (!a.professione) errs.professione = "Professione obbligatoria";
  } else if (idx === 1) {
    const r = s.residenza;
    if (!r.via) errs.via = "Via obbligatoria";
    if (!r.civico) errs.civico = "Civico obbligatorio";
    if (!/^\d{5}$/.test(r.cap)) errs.cap = "CAP 5 cifre";
    if (!r.citta) errs.citta = "Città obbligatoria";
    if (!r.provincia || r.provincia.length !== 2) errs.provincia = "Provincia (2 lettere)";
  } else if (idx === 2) {
    const p = s.patente;
    if (!p.categoria) errs.categoria = "Categoria patente obbligatoria";
    // Numero/date opzionali in MVP
  } else if (idx === 3) {
    const v = s.veicolo;
    if (v.tipologia === "auto" && !/^[A-Z]{2}\d{3}[A-Z]{2}$/.test(v.targa.toUpperCase()))
      errs.targa = "Targa formato AA000AA";
    if (!v.marca) errs.marca = "Marca obbligatoria";
    if (!v.modello) errs.modello = "Modello obbligatorio";
    if (!v.anno_immatricolazione) errs.anno_immatricolazione = "Anno obbligatorio";
    if (!v.alimentazione) errs.alimentazione = "Alimentazione obbligatoria";
  } else if (idx === 4) {
    /* documenti opzionali */
  } else if (idx === 5) {
    const st = s.storia;
    if (!st.tipo_richiesta) errs.tipo_richiesta = "Tipo richiesta obbligatorio";
    if (!st.data_decorrenza_nuova) errs.data_decorrenza_nuova = "Data decorrenza obbligatoria";
    if (!st.classe_merito_cu) errs.classe_merito_cu = "Classe di merito obbligatoria";
  } else if (idx === 6) {
    /* coperture: RCA sempre true */
  } else if (idx === 7) {
    const c = s.contatti;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email))
      errs.email = "Email non valida";
    if (!/^\+?\d{10,15}$/.test((c.telefono || "").replace(/[^\d+]/g, "")))
      errs.telefono = "Telefono non valido (es. +39...)";
  } else if (idx === 8) {
    if (!s.consensi.privacy) errs.privacy = "Privacy obbligatoria";
    if (!s.consensi.mup) errs.mup = "MUP obbligatorio";
  }
  return errs;
}

// Completeness percent for Preventivass
function calcCompleteness(s) {
  // ~28 campi chiave
  const keys = [
    s.anagrafica.nome, s.anagrafica.cognome, s.anagrafica.codice_fiscale,
    s.anagrafica.data_nascita, s.anagrafica.luogo_nascita,
    s.anagrafica.professione, s.anagrafica.titolo_studio,
    s.residenza.via, s.residenza.cap, s.residenza.citta,
    s.patente.categoria, s.patente.numero,
    s.veicolo.targa, s.veicolo.marca, s.veicolo.modello,
    s.veicolo.anno_immatricolazione, s.veicolo.alimentazione,
    s.veicolo.kw_potenza, s.veicolo.cilindrata,
    s.storia.tipo_richiesta, s.storia.classe_merito_cu,
    s.storia.data_decorrenza_nuova, s.storia.anni_continuativi !== "",
    s.contatti.email, s.contatti.telefono,
    s.consensi.privacy, s.consensi.mup,
  ];
  const ok = keys.filter((v) =>
    v !== "" && v !== null && v !== undefined && v !== 0 && v !== false
  ).length;
  return Math.round((ok / keys.length) * 100);
}

// ===========================================================================
// MAIN
// ===========================================================================
export default function PreventivoAutoPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stepIdx, setStepIdx] = useState(0);
  const [state, setState] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [draftToken, setDraftToken] = useState(null);

  // OTP flow
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [submitToken, setSubmitToken] = useState(null);
  const [otpLocked, setOtpLocked] = useState(false);
  const [otpAttemptsLeft, setOtpAttemptsLeft] = useState(3);
  const [otpDebug, setOtpDebug] = useState(null);

  // Documents uploaded
  const [docs, setDocs] = useState([]); // [{id, tipo, filename}]

  // FASE 4.2 — Pre-fill profilo cliente loggato
  const prefill = useClientProfilePrefill("auto");
  const [prefillApplied, setPrefillApplied] = useState(false);

  useEffect(() => {
    if (!prefill.loggedIn || prefill.loading || !prefill.loaded || prefillApplied) return;
    setState((s) => ({
      ...s,
      anagrafica: { ...s.anagrafica, ...(prefill.anagrafica || {}) },
      residenza: { ...s.residenza, ...(prefill.residenza || {}) },
      contatti: { ...s.contatti, ...(prefill.contatti || {}) },
    }));
    setPrefillApplied(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill.loggedIn, prefill.loaded]);

  // Marche/modelli
  const [marche, setMarche] = useState([]);
  const [modelli, setModelli] = useState([]);

  // ---- Bootstrap: load draft from URL or localStorage
  useEffect(() => {
    const urlDraft = searchParams.get("draft");
    if (urlDraft) {
      (async () => {
        try {
          const r = await api(`/draft/${urlDraft}`);
          setState({ ...EMPTY, ...r.data });
          setDraftToken(urlDraft);
          setStepIdx(Math.max(0, (r.current_step || 1) - 1));
          toast.success("Ripreso preventivo salvato");
        } catch {
          toast.error("Bozza non trovata o scaduta");
        }
      })();
      return;
    }
    try {
      const local = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
      if (local && local.state) {
        setState({ ...EMPTY, ...local.state });
        setStepIdx(local.stepIdx || 0);
        setDraftToken(local.draftToken || null);
      }
    } catch {}
  }, [searchParams]);

  // ---- Fetch marche
  useEffect(() => {
    api("/veicoli/marche").then((r) => setMarche(r.marche || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!state.veicolo.marca) { setModelli([]); return; }
    api(`/veicoli/modelli?marca=${encodeURIComponent(state.veicolo.marca)}`)
      .then((r) => setModelli(r.modelli || []))
      .catch(() => setModelli([]));
  }, [state.veicolo.marca]);

  // ---- Save draft (local + server, debounced)
  useEffect(() => {
    const t = setTimeout(async () => {
      localStorage.setItem(DRAFT_KEY,
        JSON.stringify({ state, stepIdx, draftToken }));
      try {
        const body = {
          draft_token: draftToken, step: stepIdx + 1,
          data: state, email: state.contatti.email || null,
        };
        const r = await api("/draft/save", { method: "POST", body });
        if (r.draft_token && !draftToken) setDraftToken(r.draft_token);
      } catch {}
    }, 1500);
    return () => clearTimeout(t);
  }, [state, stepIdx, draftToken]);

  const completeness = useMemo(() => calcCompleteness(state), [state]);

  // ---- FASE 4.2.11.8 AI Act badge runtime: tracking campi autofillati da OCR
  const [aiAutofilledFields, setAiAutofilledFields] = useState(() => new Set());

  // ---- FASE 4.2.11.9 — MupConsegnaModal pre-submit gate (IVASS Reg. 40/2018)
  const [mupModalOpen, setMupModalOpen] = useState(false);
  const [mupConfirmed, setMupConfirmed] = useState(false);
  const [pendingSubmitArgs, setPendingSubmitArgs] = useState(null);

  // ---- Update helper
  // Quando l'utente edita un campo, lo rimuove dal Set aiAutofilledFields
  // (edit umano = badge sparisce per quel singolo campo).
  const update = useCallback((section, patch) => {
    setState((s) => ({ ...s, [section]: { ...s[section], ...patch } }));
    setAiAutofilledFields((prev) => {
      const next = new Set(prev);
      let mutated = false;
      Object.keys(patch || {}).forEach((k) => {
        const key = `${section}.${k}`;
        if (next.has(key)) { next.delete(key); mutated = true; }
      });
      return mutated ? next : prev;
    });
  }, []);

  // ---- Auto-fill da CF
  const onCfBlur = useCallback(async () => {
    const cf = (state.anagrafica.codice_fiscale || "").toUpperCase().trim();
    if (cf.length !== 16) return;
    try {
      const r = await api("/cf/decode", {
        method: "POST", body: { codice_fiscale: cf },
      });
      const patch = {};
      if (r.data_nascita) patch.data_nascita = r.data_nascita;
      if (r.sesso) patch.sesso = r.sesso;
      if (r.luogo_nascita && !state.anagrafica.luogo_nascita)
        patch.luogo_nascita = r.luogo_nascita;
      if (Object.keys(patch).length) {
        update("anagrafica", patch);
        toast.success("Dati pre-compilati dal codice fiscale", {
          description: "Modifica solo se errati",
        });
      }
    } catch {}
  }, [state.anagrafica, update]);

  // ---- Navigation
  const goNext = useCallback(() => {
    const errs = validateStep(stepIdx, state);
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Controlla i campi evidenziati");
      return;
    }
    setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [stepIdx, state]);

  const goBack = () => setStepIdx((i) => Math.max(0, i - 1));

  // ---- OTP
  const sendOtp = async () => {
    if (!/^\+?\d{10,15}$/.test((state.contatti.telefono || "").replace(/[^\d+]/g, ""))) {
      toast.error("Inserisci un telefono valido");
      return;
    }
    setLoading(true);
    try {
      const r = await api("/otp/send", {
        method: "POST",
        body: { telefono: state.contatti.telefono, email: state.contatti.email },
      });
      setOtpSent(true);
      setOtpDebug(r.debug_code || null);
      setOtpAttemptsLeft(3);
      toast.success("OTP inviato!", {
        description: r.debug_code ? `TEST MODE: ${r.debug_code}` : "Controlla SMS",
      });
    } catch (e) {
      const c = e?.body?.detail?.error_code;
      if (c === "OTP_LOCKED") {
        setOtpLocked(true);
        toast.error("Troppi tentativi, riprova tra 15 min");
      } else {
        toast.error("Errore invio OTP");
      }
    } finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    setLoading(true);
    try {
      const r = await api("/otp/verify", {
        method: "POST",
        body: { telefono: state.contatti.telefono, codice: otpCode },
      });
      setSubmitToken(r.submit_token);
      toast.success("Telefono confermato");
    } catch (e) {
      const d = e?.body?.detail || {};
      if (d.error_code === "OTP_LOCKED_NOW" || d.error_code === "OTP_LOCKED") {
        setOtpLocked(true);
        toast.error("3 tentativi falliti. Bloccato 15 minuti.");
      } else {
        setOtpAttemptsLeft(d.attempts_left ?? 0);
        toast.error(`Codice errato. Tentativi rimasti: ${d.attempts_left ?? 0}`);
      }
    } finally { setLoading(false); }
  };

  // ---- Upload doc
  const uploadDoc = async (tipo, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File troppo grande (max 10MB)");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("tipo", tipo);
    if (draftToken) fd.append("draft_token", draftToken);
    setLoading(true);
    try {
      const r = await api("/upload-document", { method: "POST", body: fd });
      setDocs((d) => [...d, { id: r.document_id, tipo, filename: r.filename }]);
      toast.success(`${file.name} caricato`);

      // FASE 4.2.11.8 — OCR autofill via Claude Haiku (se backend ritorna extracted_metadata
      // e l'utente NON ha optato out dell'OCR AI)
      const meta = r?.extracted_metadata;
      const optedOut = isAiOcrOptedOut();
      if (meta && !optedOut) {
        const filled = applyOcrMetadata(meta, tipo);
        // Log compliance art. 12 Reg. UE 2024/1689 (fire-and-forget)
        try {
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/compliance/ai-usage-log`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              tipo: "ocr_autofill",
              modello: "claude-haiku-4.5",
              documento_tipo: tipo,
              richiesta_id: draftToken || null,
              n_campi_popolati: filled.length,
              success: true,
            }),
          });
        } catch (_) { /* log non bloccante */ }
        if (filled.length > 0) {
          toast.info(`🤖 ${filled.length} campi compilati con AI — verifica i dati estratti`);
        }
      }
    } catch (e) {
      toast.error(e?.body?.detail?.message || "Errore upload");
    } finally { setLoading(false); }
  };

  /**
   * applyOcrMetadata — mappa l'output OCR sui campi del wizard e marca
   * i field come "AI autofilled" per renderizzare il badge runtime.
   * Ritorna l'elenco dei campi effettivamente popolati.
   */
  const applyOcrMetadata = (meta, tipo) => {
    const filled = [];
    const newAiSet = new Set(aiAutofilledFields);
    const safeSet = (section, key, value) => {
      if (value == null || value === "") return;
      setState((s) => ({ ...s, [section]: { ...s[section], [key]: value } }));
      newAiSet.add(`${section}.${key}`);
      filled.push(`${section}.${key}`);
    };
    // Mapping standard per documenti supportati
    if (tipo === "patente" || tipo === "PATENTE") {
      safeSet("patente", "numero", meta.numero_patente || meta.numero);
      safeSet("patente", "categoria", meta.categoria);
      safeSet("patente", "data_rilascio", meta.data_rilascio);
      safeSet("patente", "data_scadenza", meta.data_scadenza);
    } else if (tipo === "carta_circolazione" || tipo === "libretto" || tipo === "LIBRETTO") {
      safeSet("veicolo", "targa", meta.targa);
      safeSet("veicolo", "marca", meta.marca);
      safeSet("veicolo", "modello", meta.modello);
      safeSet("veicolo", "alimentazione", meta.alimentazione);
      safeSet("veicolo", "anno_immatricolazione", meta.anno_immatricolazione);
    }
    if (filled.length > 0) setAiAutofilledFields(newAiSet);
    return filled;
  };

  // ---- Submit finale
  const handleSubmit = async (otpSkipped = false, mupBypass = false) => {
    const errs = validateStep(8, state);
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Accetta i consensi obbligatori");
      return;
    }
    // FASE 4.2.11.9 — MupConsegnaModal gating IVASS (bypass se confermato dal modal)
    if (!mupBypass && !mupConfirmed) {
      setPendingSubmitArgs({ otpSkipped });
      setMupModalOpen(true);
      return;
    }
    setLoading(true);
    try {
      const v = state.veicolo;
      // Strip empty-string date fields (Pydantic pattern non accetta '')
      const cleanPatente = (() => {
        if (!state.patente.numero && !state.patente.data_rilascio
            && !state.patente.data_scadenza) return null;
        const p = { ...state.patente };
        ["data_rilascio", "data_scadenza"].forEach((k) => {
          if (!p[k]) delete p[k];
        });
        if (!p.numero) delete p.numero;
        return p;
      })();
      const cleanStoria = { ...state.storia };
      if (!cleanStoria.data_scadenza_polizza) delete cleanStoria.data_scadenza_polizza;
      if (!cleanStoria.compagnia_attuale) delete cleanStoria.compagnia_attuale;
      const body = {
        submit_token: otpSkipped ? null : submitToken,
        otp_skipped: otpSkipped,
        email: state.contatti.email,
        telefono: state.contatti.telefono,
        anagrafica: state.anagrafica,
        residenza: state.residenza,
        patente: cleanPatente,
        veicolo: {
          ...v,
          anno_immatricolazione: parseInt(v.anno_immatricolazione, 10),
          mese_immatricolazione: parseInt(v.mese_immatricolazione, 10),
          kw_potenza: v.kw_potenza ? parseFloat(v.kw_potenza) : null,
          cilindrata: v.cilindrata ? parseInt(v.cilindrata, 10) : null,
          percorrenza_annua_km: parseInt(v.percorrenza_annua_km, 10),
        },
        storia: {
          ...cleanStoria,
          classe_merito_cu: parseInt(cleanStoria.classe_merito_cu, 10),
          sinistri_5anni: parseInt(cleanStoria.sinistri_5anni, 10) || 0,
          anni_continuativi: parseInt(cleanStoria.anni_continuativi, 10) || 0,
        },
        coperture: state.coperture,
        consensi: state.consensi,
        documents: docs.map((d) => d.id),
        draft_token: draftToken,
      };
      const r = await api("/submit", { method: "POST", body });
      localStorage.removeItem(DRAFT_KEY);
      navigate("/preventivo-auto/conferma", { state: r });
    } catch (e) {
      const code = e?.body?.detail?.error_code;
      toast.error(
        code === "SUBMIT_TOKEN_INVALID_OR_EXPIRED"
          ? "OTP scaduto. Confermalo di nuovo."
          : (e?.body?.detail?.message || "Errore invio"),
      );
    } finally { setLoading(false); }
  };

  // -------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Header />
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold text-[#163321] mb-2">
            Preventivo RC Auto
          </h1>
          <p className="text-slate-600 text-sm md:text-base">
            Compila tutti i dati e ricevi il miglior preventivo entro <b>4 ore lavorative</b>.
          </p>
        </div>

        <StepNav steps={STEPS} currentIdx={stepIdx} onJump={(i) => setStepIdx(i)} />
        <PreventivassMeter percent={completeness} />

        <form
          onSubmit={(e) => e.preventDefault()}
          className="mt-6 bg-white rounded-3xl shadow-md border border-slate-200 p-6 md:p-8"
          data-testid={`prev-auto-step-${STEPS[stepIdx].id}`}
        >
          {stepIdx === 0 && (
            <>
              {prefill.loggedIn && (
                <PrefillWelcomeBanner customerName={prefill.customerName} />
              )}
              <Step1 state={state} update={update} errors={errors}
                      onCfBlur={onCfBlur} />
            </>
          )}
          {stepIdx === 1 && (<Step2 state={state} update={update} errors={errors} />)}
          {stepIdx === 2 && (<Step3 state={state} update={update} errors={errors}
                                      aiAutofilledFields={aiAutofilledFields} />)}
          {stepIdx === 3 && (
            <Step4 state={state} update={update} errors={errors}
                    marche={marche} modelli={modelli}
                    aiAutofilledFields={aiAutofilledFields} />
          )}
          {stepIdx === 4 && (
            <Step45 docs={docs} setDocs={setDocs} onUpload={uploadDoc}
                     loading={loading} />
          )}
          {stepIdx === 5 && (<Step5 state={state} update={update} errors={errors} />)}
          {stepIdx === 6 && (<Step6 state={state} update={update} />)}
          {stepIdx === 7 && (
            <Step7 state={state} update={update} errors={errors}
                    otpSent={otpSent} otpCode={otpCode} setOtpCode={setOtpCode}
                    sendOtp={sendOtp} verifyOtp={verifyOtp}
                    submitToken={submitToken} otpLocked={otpLocked}
                    otpAttemptsLeft={otpAttemptsLeft} otpDebug={otpDebug}
                    loading={loading} />
          )}
          {stepIdx === 8 && (
            <Step8 state={state} update={update} errors={errors}
                    docs={docs}
                    submitToken={submitToken} otpLocked={otpLocked}
                    onSubmit={handleSubmit} loading={loading} />
          )}

          {/* Footer nav */}
          <div className="flex items-center justify-between gap-3 mt-8 pt-6 border-t border-slate-200">
            <Button
              type="button" variant="outline" onClick={goBack}
              disabled={stepIdx === 0}
              data-testid="prev-auto-btn-back"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
            </Button>
            {stepIdx < STEPS.length - 1 ? (
              <Button
                type="button" onClick={goNext}
                className="bg-[#163321] hover:bg-[#0e1f15]"
                data-testid="prev-auto-btn-next"
              >
                Avanti <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : null}
          </div>
        </form>

        {/* FASE 4.2.11.9 — MupConsegnaModal gating IVASS prima del submit finale */}
        <MupConsegnaModal
          open={mupModalOpen}
          onClose={() => { setMupModalOpen(false); setPendingSubmitArgs(null); }}
          onConfirm={() => {
            setMupConfirmed(true);
            setMupModalOpen(false);
            const args = pendingSubmitArgs || { otpSkipped: false };
            setPendingSubmitArgs(null);
            // FASE 4.2.11.11 fix: bypass esplicito via flag, non state stale.
            // setTimeout(0) NON garantisce che la closure di handleSubmit veda
            // il mupConfirmed=true (closure cattura mupConfirmed=false del render
            // precedente), causando re-apertura del modal e mancato submit.
            handleSubmit(args.otpSkipped, true);
          }}
          prodotto="auto"
          richiestaId={draftToken}
          customerId={null}
        />
      </main>
      <Footer />
    </div>
  );
}

// ===========================================================================
// STEP 1: Dati Personali
// ===========================================================================
function Step1({ state, update, errors, onCfBlur }) {
  const a = state.anagrafica;
  const autofilledFromCf = (k) => (a.codice_fiscale.length === 16 && a[k]);
  return (
    <>
      <h2 className="text-xl font-semibold text-[#163321] mb-1">Dati personali</h2>
      <p className="text-sm text-slate-500 mb-6">Inserisci prima il codice fiscale: alcuni campi saranno pre-compilati automaticamente.</p>
      <div className="grid md:grid-cols-2 gap-4">
        <FieldText label="Nome" testid="nome" value={a.nome}
                    onChange={(v) => update("anagrafica", { nome: v })}
                    error={errors.nome} required />
        <FieldText label="Cognome" testid="cognome" value={a.cognome}
                    onChange={(v) => update("anagrafica", { cognome: v })}
                    error={errors.cognome} required />
        <div className="md:col-span-2">
          <Label className="text-sm font-medium text-[#163321] mb-1.5 block">
            Codice Fiscale <span className="text-rose-500">*</span>
          </Label>
          <Input
            data-testid="field-cf"
            value={a.codice_fiscale}
            onChange={(e) => update("anagrafica", {
              codice_fiscale: e.target.value.toUpperCase().slice(0, 16),
            })}
            onBlur={onCfBlur}
            placeholder="RSSMRA85M50F839Y"
            className={errors.codice_fiscale && "border-rose-300"}
            maxLength={16}
          />
          {errors.codice_fiscale && (
            <p className="text-xs text-rose-500 mt-1">{errors.codice_fiscale}</p>
          )}
          {a.codice_fiscale.length === 16 && (
            <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Auto-compilazione attiva
            </p>
          )}
        </div>
        <FieldDate label="Data nascita" testid="data-nascita"
                    locked={autofilledFromCf("data_nascita")}
                    value={a.data_nascita} error={errors.data_nascita}
                    onChange={(v) => update("anagrafica", { data_nascita: v })} required />
        <FieldText label="Luogo nascita" testid="luogo-nascita"
                    value={a.luogo_nascita}
                    locked={autofilledFromCf("luogo_nascita")}
                    onChange={(v) => update("anagrafica", { luogo_nascita: v })}
                    error={errors.luogo_nascita} required />
        <FieldSelect label="Sesso" testid="sesso" value={a.sesso}
                      locked={autofilledFromCf("sesso")}
                      onChange={(v) => update("anagrafica", { sesso: v })}
                      options={SESSI.map((s) => [s.v, s.l])}
                      error={errors.sesso} required />
        <FieldSelect label="Stato civile" testid="stato-civile"
                      value={a.stato_civile}
                      onChange={(v) => update("anagrafica", { stato_civile: v })}
                      options={STATI_CIVILI}
                      error={errors.stato_civile} required />
        <FieldSelect label="Titolo studio" testid="titolo-studio"
                      value={a.titolo_studio}
                      onChange={(v) => update("anagrafica", { titolo_studio: v })}
                      options={TITOLI}
                      error={errors.titolo_studio} required />
        <FieldText label="Professione" testid="professione"
                    value={a.professione}
                    onChange={(v) => update("anagrafica", { professione: v })}
                    error={errors.professione} required />
      </div>
    </>
  );
}

// ===========================================================================
// STEP 2: Residenza
// ===========================================================================
function Step2({ state, update, errors }) {
  const r = state.residenza;
  return (
    <>
      <h2 className="text-xl font-semibold text-[#163321] mb-6">Residenza</h2>
      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <FieldText label="Via" testid="via" value={r.via}
                      onChange={(v) => update("residenza", { via: v })}
                      error={errors.via} required />
        </div>
        <FieldText label="Civico" testid="civico" value={r.civico}
                    onChange={(v) => update("residenza", { civico: v })}
                    error={errors.civico} required />
        <FieldText label="CAP" testid="cap" value={r.cap}
                    onChange={(v) => update("residenza", { cap: v.replace(/\D/g, "").slice(0, 5) })}
                    error={errors.cap} required />
        <div className="md:col-span-1">
          <FieldText label="Città" testid="citta" value={r.citta}
                      onChange={(v) => update("residenza", { citta: v })}
                      error={errors.citta} required />
        </div>
        <FieldText label="Provincia (sigla)" testid="provincia"
                    value={r.provincia}
                    onChange={(v) => update("residenza", { provincia: v.toUpperCase().slice(0, 2) })}
                    error={errors.provincia} required />
      </div>
    </>
  );
}

// ===========================================================================
// STEP 3: Patente
// ===========================================================================
function Step3({ state, update, errors, aiAutofilledFields }) {
  const p = state.patente;
  const aiSet = aiAutofilledFields || new Set();
  const patenteAi = aiSet.has("patente.numero") || aiSet.has("patente.data_rilascio")
    || aiSet.has("patente.data_scadenza") || aiSet.has("patente.categoria");
  return (
    <>
      <h2 className="text-xl font-semibold text-[#163321] mb-1">Patente di guida</h2>
      <p className="text-sm text-slate-500 mb-6">Inserisci almeno la categoria. Numero e date sono consigliati ma non obbligatori.</p>
      <AiAutofillBadge visible={patenteAi} model="claude-haiku-4.5" />
      <div className="grid md:grid-cols-2 gap-4">
        <FieldSelect label="Categoria" testid="cat-patente"
                      value={p.categoria}
                      onChange={(v) => update("patente", { categoria: v })}
                      options={CATEGORIE_PATENTE.map((c) => [c, c])}
                      error={errors.categoria} required />
        <FieldText label="Numero patente" testid="num-patente"
                    value={p.numero}
                    onChange={(v) => update("patente", { numero: v.toUpperCase() })} />
        <FieldDate label="Data rilascio" testid="rilascio-patente"
                    value={p.data_rilascio}
                    onChange={(v) => update("patente", { data_rilascio: v })} />
        <FieldDate label="Data scadenza" testid="scadenza-patente"
                    value={p.data_scadenza}
                    onChange={(v) => update("patente", { data_scadenza: v })} />
      </div>
    </>
  );
}

// ===========================================================================
// STEP 4: Veicolo
// ===========================================================================
function Step4({ state, update, errors, marche, modelli, aiAutofilledFields }) {
  const v = state.veicolo;
  const aiSet = aiAutofilledFields || new Set();
  const veicoloAi = aiSet.has("veicolo.targa") || aiSet.has("veicolo.marca")
    || aiSet.has("veicolo.modello") || aiSet.has("veicolo.alimentazione");
  // NB: il return originale di Step4 continua sotto, il banner viene iniettato
  //     subito dopo l'apertura del fragment / heading.
  const years = Array.from({ length: 30 }, (_, i) => 2026 - i);
  return (
    <>
      <h2 className="text-xl font-semibold text-[#163321] mb-6">Dati veicolo</h2>
      <AiAutofillBadge visible={veicoloAi} model="claude-haiku-4.5" />
      <div className="grid md:grid-cols-2 gap-4">
        <FieldText label="Targa" testid="targa"
                    value={v.targa}
                    onChange={(t) => update("veicolo", { targa: t.toUpperCase().replace(/\s/g, "") })}
                    error={errors.targa} required placeholder="AA000AA" />
        <FieldSelect label="Tipologia" testid="tip-veicolo" value={v.tipologia}
                      onChange={(x) => update("veicolo", { tipologia: x })}
                      options={[["auto", "Auto"], ["moto", "Moto"],
                                 ["ciclomotore", "Ciclomotore"], ["camper", "Camper"]]}
                      required />
        <FieldSelect label="Marca" testid="marca" value={v.marca}
                      onChange={(x) => update("veicolo", { marca: x, modello: "" })}
                      options={marche.map((m) => [m, m])}
                      error={errors.marca} required />
        {modelli.length > 0 ? (
          <FieldSelect label="Modello" testid="modello" value={v.modello}
                        onChange={(x) => update("veicolo", { modello: x })}
                        options={modelli.map((m) => [m, m])}
                        error={errors.modello} required />
        ) : (
          <FieldText label="Modello" testid="modello"
                      value={v.modello}
                      onChange={(x) => update("veicolo", { modello: x })}
                      error={errors.modello} required />
        )}
        <FieldText label="Allestimento (opzionale)" testid="allestimento"
                    value={v.allestimento}
                    onChange={(x) => update("veicolo", { allestimento: x })} />
        <FieldSelect label="Anno immatricolazione" testid="anno"
                      value={String(v.anno_immatricolazione)}
                      onChange={(x) => update("veicolo", { anno_immatricolazione: x })}
                      options={years.map((y) => [String(y), String(y)])}
                      error={errors.anno_immatricolazione} required />
        <FieldSelect label="Alimentazione" testid="alim" value={v.alimentazione}
                      onChange={(x) => update("veicolo", { alimentazione: x })}
                      options={ALIMENTAZIONI}
                      error={errors.alimentazione} required />
        <FieldText label="Potenza (kW)" testid="kw" inputMode="numeric"
                    value={v.kw_potenza}
                    onChange={(x) => update("veicolo", { kw_potenza: x.replace(/[^\d.]/g, "") })} />
        <FieldText label="Cilindrata (cc)" testid="cc" inputMode="numeric"
                    value={v.cilindrata}
                    onChange={(x) => update("veicolo", { cilindrata: x.replace(/\D/g, "") })} />
        <FieldSelect label="Uso veicolo" testid="uso" value={v.uso}
                      onChange={(x) => update("veicolo", { uso: x })}
                      options={USI} required />
        <FieldText label="Km annui stimati" testid="km" inputMode="numeric"
                    value={v.percorrenza_annua_km}
                    onChange={(x) => update("veicolo", { percorrenza_annua_km: x.replace(/\D/g, "") })} />
        <FieldSelect label="Tipologia guida" testid="guida"
                      value={v.tipologia_guida}
                      onChange={(x) => update("veicolo", { tipologia_guida: x })}
                      options={GUIDA} required />
        <div className="md:col-span-2 flex items-center gap-2 pt-2">
          <Checkbox
            id="antifurto" checked={v.antifurto}
            onCheckedChange={(c) => update("veicolo", { antifurto: !!c })}
            data-testid="check-antifurto"
          />
          <Label htmlFor="antifurto" className="text-sm">Antifurto installato</Label>
        </div>
        {v.antifurto && (
          <div className="md:col-span-2">
            <FieldText label="Tipo antifurto" testid="antifurto-tipo"
                        value={v.antifurto_tipo}
                        onChange={(x) => update("veicolo", { antifurto_tipo: x })}
                        placeholder="es. satellitare, meccanico..." />
          </div>
        )}
      </div>
    </>
  );
}

// ===========================================================================
// STEP 4.5: Documenti opzionali
// ===========================================================================
function Step45({ docs, setDocs, onUpload, loading }) {
  return (
    <>
      <h2 className="text-xl font-semibold text-[#163321] mb-1">Documenti opzionali</h2>
      <p className="text-sm text-slate-500 mb-4">
        Caricali ora per accelerare il preventivo (PDF/JPG/PNG, max 10MB). Puoi saltare e inviarli dopo via Area Cliente.
      </p>
      {/* FASE 4.2.11.8 — toggle opt-out AI OCR (art. 50 AI Act) */}
      <div className="mb-5 flex flex-wrap items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
        <span className="text-xs text-amber-900 flex-1">
          🤖 I documenti caricati possono essere analizzati con AI (Claude Haiku) per pre-compilare i campi del questionario.
        </span>
        <AiOcrOptOutToggle />
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {DOC_TYPES.map((d) => {
          const uploaded = docs.find((x) => x.tipo === d.v);
          return (
            <label
              key={d.v}
              data-testid={`upload-${d.v}`}
              className={[
                "rounded-2xl border-2 border-dashed p-4 flex flex-col items-center justify-center text-center cursor-pointer transition-all",
                uploaded
                  ? "border-emerald-300 bg-emerald-50"
                  : "border-slate-300 hover:border-[#163321] hover:bg-slate-50",
              ].join(" ")}
            >
              <input
                type="file" accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                onChange={(e) => onUpload(d.v, e.target.files?.[0])}
              />
              {uploaded ? (
                <>
                  <FileCheck2 className="w-8 h-8 text-emerald-600 mb-2" />
                  <p className="text-sm font-medium text-emerald-800">{d.l}</p>
                  <p className="text-xs text-emerald-600 mt-1 truncate w-full">{uploaded.filename}</p>
                </>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-slate-400 mb-2" />
                  <p className="text-sm font-medium">{d.l}</p>
                  <p className="text-xs text-slate-500 mt-1">Clicca per caricare</p>
                </>
              )}
            </label>
          );
        })}
      </div>
      {loading && (
        <p className="text-sm text-slate-500 mt-4 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Caricamento in corso…
        </p>
      )}
    </>
  );
}

// ===========================================================================
// STEP 5: Storia assicurativa
// ===========================================================================
function Step5({ state, update, errors }) {
  const s = state.storia;
  return (
    <>
      <h2 className="text-xl font-semibold text-[#163321] mb-6">Storia assicurativa</h2>
      <div className="grid md:grid-cols-2 gap-4">
        <FieldSelect label="Tipo richiesta" testid="tipo-rich"
                      value={s.tipo_richiesta}
                      onChange={(v) => update("storia", { tipo_richiesta: v })}
                      options={TIPI_RICHIESTA}
                      error={errors.tipo_richiesta} required />
        <FieldText label="Compagnia attuale (opz.)" testid="compagnia"
                    value={s.compagnia_attuale}
                    onChange={(v) => update("storia", { compagnia_attuale: v })} />
        <FieldDate label="Scadenza polizza attuale" testid="scad-pol"
                    value={s.data_scadenza_polizza}
                    onChange={(v) => update("storia", { data_scadenza_polizza: v })} />
        <FieldDate label="Decorrenza nuova polizza" testid="dec-nuova"
                    value={s.data_decorrenza_nuova}
                    onChange={(v) => update("storia", { data_decorrenza_nuova: v })}
                    error={errors.data_decorrenza_nuova} required />
        <FieldSelect label="Classe Merito CU (1=migliore)" testid="cu"
                      value={String(s.classe_merito_cu)}
                      onChange={(v) => update("storia", { classe_merito_cu: v })}
                      options={Array.from({ length: 18 }, (_, i) => [String(i + 1), `${i + 1}ª CU`])}
                      error={errors.classe_merito_cu} required />
        <FieldText label="Sinistri ultimi 5 anni" testid="sinistri"
                    inputMode="numeric"
                    value={s.sinistri_5anni}
                    onChange={(v) => update("storia", { sinistri_5anni: v.replace(/\D/g, "") || 0 })} />
        <FieldText label="Anni assicurato continuativi" testid="anni-cont"
                    inputMode="numeric"
                    value={s.anni_continuativi}
                    onChange={(v) => update("storia", { anni_continuativi: v.replace(/\D/g, "") || 0 })} />
      </div>
    </>
  );
}

// ===========================================================================
// STEP 6: Coperture
// ===========================================================================
function Step6({ state, update }) {
  const c = state.coperture;
  const COPS = [
    ["furto_incendio", "Furto e incendio"],
    ["kasko", "Kasko completa"],
    ["mini_kasko", "Mini Kasko (collisione)"],
    ["cristalli", "Cristalli"],
    ["assistenza_stradale", "Assistenza stradale"],
    ["tutela_legale", "Tutela legale"],
    ["infortuni_conducente", "Infortuni conducente"],
    ["atti_vandalici", "Atti vandalici"],
    ["eventi_naturali", "Eventi naturali"],
  ];
  return (
    <>
      <h2 className="text-xl font-semibold text-[#163321] mb-2">Coperture desiderate</h2>
      <p className="text-sm text-slate-500 mb-6">
        RCA inclusa. Massimale: <b>€ {c.massimale_rca_eur.toLocaleString("it-IT")}</b>.
      </p>
      <div className="grid md:grid-cols-2 gap-3">
        {COPS.map(([k, l]) => (
          <label
            key={k}
            data-testid={`cop-${k}`}
            className={[
              "flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all",
              c[k] ? "border-[#163321] bg-emerald-50" : "border-slate-200 hover:border-slate-300",
            ].join(" ")}
          >
            <Checkbox
              checked={!!c[k]}
              onCheckedChange={(v) => update("coperture", { [k]: !!v })}
            />
            <span className="text-sm font-medium text-slate-800">{l}</span>
          </label>
        ))}
      </div>
    </>
  );
}

// ===========================================================================
// STEP 7: Contatti + OTP
// ===========================================================================
function Step7({
  state, update, errors,
  otpSent, otpCode, setOtpCode, sendOtp, verifyOtp,
  submitToken, otpLocked, otpAttemptsLeft, otpDebug, loading,
}) {
  const c = state.contatti;
  return (
    <>
      <h2 className="text-xl font-semibold text-[#163321] mb-6">Come ti contattiamo</h2>
      <div className="grid md:grid-cols-2 gap-4">
        <FieldText label="Email" testid="email" value={c.email}
                    onChange={(v) => update("contatti", { email: v })}
                    placeholder="tu@email.it"
                    error={errors.email} required />
        <FieldText label="Telefono cellulare" testid="telefono"
                    value={c.telefono}
                    onChange={(v) => update("contatti", { telefono: v })}
                    placeholder="+39 333 1234567"
                    error={errors.telefono} required />
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5">
        <h3 className="text-base font-semibold text-[#163321] mb-2 flex items-center gap-2">
          <Phone className="w-4 h-4" /> Conferma il numero via SMS
        </h3>
        {!submitToken && (
          <>
            {!otpSent ? (
              <>
                <p className="text-sm text-slate-600 mb-3">
                  Ti inviamo un codice OTP a 6 cifre per validare il contatto.
                </p>
                <Button
                  type="button" onClick={sendOtp}
                  disabled={loading || otpLocked}
                  className="bg-[#163321] hover:bg-[#0e1f15]"
                  data-testid="prev-auto-send-otp"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Invia OTP"}
                </Button>
              </>
            ) : (
              <>
                <Label className="text-sm font-medium mb-1.5 block">Codice OTP</Label>
                <div className="flex gap-2">
                  <Input
                    data-testid="prev-auto-otp-input"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="123456" maxLength={6} inputMode="numeric"
                    className="flex-1"
                  />
                  <Button
                    type="button" onClick={verifyOtp}
                    disabled={loading || otpLocked || otpCode.length !== 6}
                    className="bg-[#163321]"
                    data-testid="prev-auto-verify-otp"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verifica"}
                  </Button>
                </div>
                {otpDebug && (
                  <p className="text-xs text-amber-600 mt-2">
                    TEST MODE: codice <b>{otpDebug}</b>
                  </p>
                )}
                <p className="text-xs text-slate-500 mt-2">
                  Tentativi rimasti: {otpAttemptsLeft}
                </p>
              </>
            )}
          </>
        )}
        {submitToken && (
          <Alert className="border-emerald-200 bg-emerald-50">
            <Check className="w-4 h-4 text-emerald-600" />
            <AlertDescription className="text-emerald-800">
              Telefono confermato! Procedi al riepilogo.
            </AlertDescription>
          </Alert>
        )}
        {otpLocked && (
          <Alert className="border-rose-200 bg-rose-50 mt-3">
            <AlertTriangle className="w-4 h-4 text-rose-600" />
            <AlertDescription className="text-rose-800 text-sm">
              Troppi tentativi falliti. Riprova tra 15 minuti, oppure invia senza OTP — il consulente ti contatterà manualmente.
            </AlertDescription>
          </Alert>
        )}
      </div>
    </>
  );
}

// ===========================================================================
// STEP 8: Riepilogo + Consensi
// ===========================================================================
function Step8({ state, errors, update, docs, submitToken, otpLocked, onSubmit, loading }) {
  const summary = [
    ["Nome", `${state.anagrafica.nome} ${state.anagrafica.cognome}`],
    ["CF", state.anagrafica.codice_fiscale],
    ["Residenza", `${state.residenza.via} ${state.residenza.civico}, ${state.residenza.cap} ${state.residenza.citta} (${state.residenza.provincia})`],
    ["Veicolo", `${state.veicolo.marca} ${state.veicolo.modello} (${state.veicolo.targa})`],
    ["Anno", state.veicolo.anno_immatricolazione],
    ["Alimentazione", state.veicolo.alimentazione],
    ["Classe Merito", `${state.storia.classe_merito_cu}ª CU`],
    ["Compagnia attuale", state.storia.compagnia_attuale || "—"],
    ["Decorrenza nuova", state.storia.data_decorrenza_nuova],
    ["Email", state.contatti.email],
    ["Telefono", state.contatti.telefono],
    ["Documenti caricati", `${docs.length}`],
  ];
  const canSubmit = !!submitToken;
  const canFallback = otpLocked || !submitToken;
  return (
    <>
      <h2 className="text-xl font-semibold text-[#163321] mb-4">Riepilogo richiesta</h2>
      <div data-testid="prev-auto-summary" className="rounded-2xl border border-slate-200 bg-slate-50 p-5 mb-6">
        <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
          {summary.map(([k, v]) => (
            <div key={k} className="flex items-baseline gap-2">
              <dt className="text-xs text-slate-500 w-32 shrink-0">{k}</dt>
              <dd className="text-sm text-slate-800 font-medium break-all">{v || "—"}</dd>
            </div>
          ))}
        </dl>
      </div>

      <h3 className="text-base font-semibold text-[#163321] mb-2 flex items-center gap-2">
        <ShieldCheck className="w-4 h-4" /> Consensi
      </h3>
      <div className="space-y-3">
        <ConsentRow
          k="privacy"
          label={<>Ho letto l'<LegalLink slug="privacy">informativa privacy</LegalLink> e acconsento al trattamento dei dati personali</>}
          required errors={errors} state={state} update={update}
        />
        <ConsentRow
          k="mup"
          label={<>Ho letto il <LegalLink slug="mup">Modulo Unico di Proposta (MUP)</LegalLink></>}
          required errors={errors} state={state} update={update}
        />
        <ConsentRow
          k="marketing"
          label={<>Acconsento alle <LegalLink slug="marketing">comunicazioni marketing</LegalLink> e promozioni (opzionale)</>}
          errors={errors} state={state} update={update}
        />
        <ConsentRow
          k="profilazione"
          label={<>Acconsento alla <LegalLink slug="profilazione">profilazione</LegalLink> per offerte personalizzate (opzionale)</>}
          errors={errors} state={state} update={update}
        />
      </div>

      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <Button
          type="button" onClick={() => onSubmit(false)}
          disabled={!canSubmit || loading}
          className="bg-[#163321] hover:bg-[#0e1f15] flex-1 text-base py-6"
          data-testid="prev-auto-submit"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
            <>Invia richiesta <Send className="w-5 h-5 ml-2" /></>
          )}
        </Button>
        {canFallback && (
          <Button
            type="button" onClick={() => onSubmit(true)}
            disabled={loading}
            variant="outline"
            data-testid="prev-auto-submit-skip-otp"
          >
            Procedo senza OTP
          </Button>
        )}
      </div>
      {!canSubmit && !canFallback && (
        <p className="text-xs text-amber-600 mt-3">
          Torna allo step "Contatti" per confermare il telefono via OTP.
        </p>
      )}
    </>
  );
}

function LegalLink({ slug, children }) {
  return <LegalLinkBubble slug={slug}>{children}</LegalLinkBubble>;
}

function ConsentRow({ k, label, required, errors, state, update }) {
  return (
    <label
      data-testid={`consent-${k}`}
      className={[
        "flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all",
        state.consensi[k] ? "border-emerald-300 bg-emerald-50" : "border-slate-200",
        errors[k] && "border-rose-300 bg-rose-50",
      ].filter(Boolean).join(" ")}
    >
      <Checkbox
        checked={!!state.consensi[k]} className="mt-1"
        onCheckedChange={(v) => update("consensi", { [k]: !!v })}
      />
      <span className="text-sm text-slate-700">
        {label} {required && <span className="text-rose-500">*</span>}
        {errors[k] && (
          <span className="block text-xs text-rose-500 mt-0.5">{errors[k]}</span>
        )}
      </span>
    </label>
  );
}

// ===========================================================================
// REUSABLE FIELDS
// ===========================================================================
function FieldText({
  label, testid, value, onChange, error, required, placeholder, inputMode,
  locked,
}) {
  return (
    <div>
      <Label className="text-sm font-medium text-[#163321] mb-1.5 block">
        {label} {required && <span className="text-rose-500">*</span>}
        {locked && (
          <span className="inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-100 text-emerald-700">
            <Lock className="w-2.5 h-2.5" /> da CF
          </span>
        )}
      </Label>
      <Input
        data-testid={`field-${testid}`}
        value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} inputMode={inputMode}
        className={error ? "border-rose-300" : ""}
      />
      {error && <p className="text-xs text-rose-500 mt-1">{error}</p>}
    </div>
  );
}

function FieldDate({ label, testid, value, onChange, error, required, locked }) {
  return (
    <div>
      <Label className="text-sm font-medium text-[#163321] mb-1.5 block">
        {label} {required && <span className="text-rose-500">*</span>}
        {locked && (
          <span className="inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-100 text-emerald-700">
            <Lock className="w-2.5 h-2.5" /> da CF
          </span>
        )}
      </Label>
      <Input
        data-testid={`field-${testid}`}
        type="date" value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className={error ? "border-rose-300" : ""}
      />
      {error && <p className="text-xs text-rose-500 mt-1">{error}</p>}
    </div>
  );
}

function FieldSelect({
  label, testid, value, onChange, options, error, required, locked,
}) {
  return (
    <div>
      <Label className="text-sm font-medium text-[#163321] mb-1.5 block">
        {label} {required && <span className="text-rose-500">*</span>}
        {locked && (
          <span className="inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-100 text-emerald-700">
            <Lock className="w-2.5 h-2.5" /> da CF
          </span>
        )}
      </Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          data-testid={`field-${testid}`}
          className={error ? "border-rose-300" : ""}
        >
          <SelectValue placeholder="Seleziona…" />
        </SelectTrigger>
        <SelectContent>
          {options.map(([v, l]) => (
            <SelectItem key={v} value={v}>{l}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error && <p className="text-xs text-rose-500 mt-1">{error}</p>}
    </div>
  );
}
